import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ProjectModel } from 'types';
import { listOsts, getOst, createOst, deleteOst } from '../../api/projects';
import { useStore } from '../../store/useStore';
import { usePricingStore } from '../../pricing/pricingStore';
import { UpgradeMessageKey } from '../../pricing/upgradeMessages';
import { FREE_OST_LIMIT } from '../../pricing/pricingTiers';
import { resolveEffectiveTier } from '../../pricing';
import { Modal, FormSection, FormRow } from 'components/ui';

type ProjectMenuProps = {
    projects: ProjectModel[];
    activeProject: ProjectModel | null;
    projectMenuOpen: boolean;
    setProjectMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    projectName: string;
    setProjectName: (value: string) => void;
    handleSelectProject: (projectId: string) => void;
    handleDeleteProject: () => void;
    handleCreateProject: () => void;
    handleExportProject: () => void;
    handleImportProject: (file: File) => void;
    importDisabled: boolean;
};

/**
 * Extracted UI for project selection and management in App topbar.
 */
export default function ProjectMenu({
    projects,
    activeProject,
    projectMenuOpen,
    setProjectMenuOpen,
    projectName,
    setProjectName,
    handleSelectProject,
    handleDeleteProject,
    handleCreateProject,
    handleExportProject,
    handleImportProject,
    importDisabled,
}: ProjectMenuProps) {
    const activeOstId = useStore((s) => s.activeOstId);
    const setActiveOst = useStore((s) => s.setActiveOst);
    const setOst = useStore((s) => s.setOst);
    const setInsightsPanelMinimized = useStore((s) => s.setInsightsPanelMinimized);
    const setTreeLoading = useStore((s) => s.setTreeLoading);
    const user = useStore((s) => s.user);
    // Use effective tier so power_user/zeytune_team bypass the tree limit check.
    const plan = resolveEffectiveTier(user);
    const showUpgradePrompt = usePricingStore((s) => s.showUpgradePrompt);

    const [osts, setOsts] = useState<Array<{ _id: string; name: string }>>([]);
    const [newTreeName, setNewTreeName] = useState('');
    const projectMenuRef = useRef<HTMLDivElement | null>(null);
    const importInputRef = useRef<HTMLInputElement | null>(null);

    const onImportClick = () => {
        if (importDisabled) return;
        importInputRef.current?.click();
    };

    const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleImportProject(file);
        }
        // Reset file input so same file can be re-chosen if needed.
        e.target.value = '';
    };

    // Load OSTs for the active project when it becomes available
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!activeProject || !user) {
                setOsts([]);
                return;
            }
            const res = await listOsts(activeProject._id);
            if (cancelled) return;
            if (res && Array.isArray(res)) {
                setOsts(res as any[]);
            } else {
                setOsts([]);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [activeProject, user]);

    const onSelectOst = async (ostId: string) => {
        if (!activeProject || !user) return;
        if (!ostId) return;
        // Fetch OST payload and set into store with loading indicator
        setTreeLoading(true);
        try {
            const res = await getOst(activeProject._id, ostId);
            if (res && !(res as any).error) {
                const o = res as any;
                // Merge project-level insights if present
                const merged = { ...(o.ost || {}), insights: (activeProject.insights || []).length ? activeProject.insights : (o.ost?.insights || []) };
                setActiveOst(ostId);
                setOst(merged);
            }
        } finally {
            setTreeLoading(false);
        }
    };

    const onCreateOst = async () => {
        if (!activeProject || !user) return;
        if (plan === 'free' && osts.length >= FREE_OST_LIMIT) {
            showUpgradePrompt(UpgradeMessageKey.TreeLimit);
            return;
        }
        const name = (newTreeName || '').trim();
        if (!name) return;
        setTreeLoading(true);
        try {
            const res = await createOst(activeProject._id, name, {});
            if (res && !(res as any).error) {
                const newOst = res as any;
                // Auto-select the newly created tree and load its OST state.
                setActiveOst(String(newOst._id));
                const merged = { ...(newOst.ost || {}), insights: activeProject.insights || [] };
                setOst(merged);
                // Keep InsightsPanel minimized when switching to a freshly created tree.
                setInsightsPanelMinimized(true);
                // reset input
                setNewTreeName('');
                // Reload OST list for UI
                const updated = await listOsts(activeProject._id);
                if (Array.isArray(updated)) setOsts(updated as any[]);
            }
        } finally {
            setTreeLoading(false);
        }
    };

    const onDeleteOst = async () => {
        if (!activeProject || !user) return;
        if (!activeOstId) {
            window.alert('No tree selected to delete.');
            return;
        }
        const ok = window.confirm('Delete this tree? This action cannot be undone.');
        if (!ok) return;
        const res = await deleteOst(activeProject._id, activeOstId);
        if (res && !(res as any).error) {
            // Refresh list and clear selection if the deleted OST was active
            const updated = await listOsts(activeProject._id);
            if (Array.isArray(updated)) setOsts(updated as any[]);
            if (activeOstId) {
                setActiveOst(null);
                // Clear canvas state
                setOst({ outcome: null, opportunities: [], solutions: [], userStories: [], positions: {}, nodePanelPosition: { x: 20, y: 120 }, solutionChildType: 'story' } as any);
            }
        } else {
            window.alert((res as any)?.error || 'Failed to delete tree');
        }
    };


    // Close project menu when clicking outside this component
    useEffect(() => {
        if (!projectMenuOpen) return;

        const onOutsideClick = (event: MouseEvent) => {
            if (projectMenuRef.current && !projectMenuRef.current.contains(event.target as Node)) {
                setProjectMenuOpen(false);
            }
        };

        window.addEventListener('mousedown', onOutsideClick);
        return () => window.removeEventListener('mousedown', onOutsideClick);
    }, [projectMenuOpen, setProjectMenuOpen]);

    return (
        <div className='app-project-menu-wrapper' ref={projectMenuRef}>
            {/* SVG icon button — acts as the top-bar trigger */}
            <button
                className='app-top-action-btn'
                onClick={() => setProjectMenuOpen((o) => !o)}
                title='Project config'
                aria-label='Project config'
            >
                <svg width='24' height='24' viewBox='0 0 16 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                        d='M1 3.5C1 2.67 1.67 2 2.5 2H6l1.5 1.5H13.5C14.33 3.5 15 4.17 15 5v6.5C15 12.33 14.33 13 13.5 13h-11C1.67 13 1 12.33 1 11.5V3.5Z'
                        stroke='currentColor'
                        strokeWidth='1.9'
                        strokeLinejoin='round'
                    />
                </svg>
            </button>
            {projectMenuOpen && (
                <Modal
                    title='Project config'
                    onClose={() => setProjectMenuOpen(false)}
                    headerVariant='default'
                    closeOnOverlayClick={true}
                    overlayProps={{ role: 'dialog', 'aria-modal': 'true', className: 'modal-overlay--higher' }}
                    headerId='project-config-modal-title'
                    closeButtonClassName='project-modal-close'
                >
                    <FormSection>
                        <div className='form-section-header'>
                            <label className='form-label'>Projects</label>
                        </div>

                        <FormRow>
                            <div className='app-project-select-row'>
                                <select
                                    className='form-select app-project-select'
                                    value={activeProject?._id || ''}
                                    onChange={(e) => {
                                        handleSelectProject(e.target.value);
                                        setProjectMenuOpen(false);
                                    }}
                                >
                                    <option value='' disabled>
                                        Select project…
                                    </option>
                                    {projects.map((p) => (
                                        <option key={p._id} value={p._id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className='app-project-icon-btn app-project-icon-btn--blue'
                                    onClick={handleExportProject}
                                    disabled={!activeProject}
                                    aria-label='Export project'
                                    title='Export project (.zey)'
                                >
                                    ⬆
                                </button>
                                <button
                                    className='app-project-icon-btn app-project-icon-btn--blue'
                                    onClick={onImportClick}
                                    disabled={importDisabled}
                                    aria-label='Import project'
                                    title='Import project (.zey)'
                                >
                                    ⬇
                                </button>
                                <button
                                    className='app-project-icon-btn app-project-icon-btn--red'
                                    onClick={handleDeleteProject}
                                    disabled={!activeProject}
                                    aria-label='Delete project'
                                    title='Delete project'
                                >
                                    ✕
                                </button>
                                <input
                                    ref={importInputRef}
                                    type='file'
                                    accept='.zey'
                                    className="visually-hidden-input"
                                    onChange={onFileChange}
                                />
                            </div>
                        </FormRow>

                        <FormRow className='app-project-form'>
                            <input
                                className='form-input app-project-input'
                                value={projectName}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
                                placeholder='New project name…'
                            />
                            <button
                                type='button'
                                className='app-project-icon-btn app-project-icon-btn--green'
                                onClick={handleCreateProject}
                                aria-label='Create project'
                                title='Create project'
                            >
                                +
                            </button>
                        </FormRow>
                    </FormSection>

                    {/* <hr className='app-project-separator' /> */}

                    {activeProject && (
                        <FormSection>
                            <div className='form-section-header'>
                                <label className='form-label'>Trees</label>
                            </div>

                            <FormRow>
                                <div className='app-project-select-row'>
                                    <select
                                        className='form-select app-project-select'
                                        value={activeOstId || ''}
                                        onChange={(e) => {
                                            onSelectOst(e.target.value);
                                            setProjectMenuOpen(false);
                                        }}
                                    >
                                        <option value=''>Select tree…</option>
                                        {osts.map((o) => (
                                            <option key={o._id} value={o._id}>
                                                {o.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type='button'
                                        onClick={onDeleteOst}
                                        className='app-project-icon-btn app-project-icon-btn--red'
                                        disabled={!activeOstId}
                                        aria-label='Delete tree'
                                        title='Delete tree'
                                    >
                                        ✕
                                    </button>
                                </div>
                            </FormRow>

                            <FormRow className='app-project-form'>
                                <input
                                    className='form-input app-project-input'
                                    value={newTreeName}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNewTreeName(e.target.value)}
                                    placeholder='New tree name…'
                                />
                                <button
                                    type='button'
                                    onClick={onCreateOst}
                                    className='app-project-icon-btn app-project-icon-btn--green'
                                    disabled={!newTreeName.trim()}
                                    aria-label='Create tree'
                                    title='Create tree'
                                >
                                    +
                                </button>
                            </FormRow>
                        </FormSection>
                    )}
                </Modal>
            )}
        </div>
    );
}
